import { defineNuxtPlugin, useRuntimeConfig, useRouter, nextTick, watchEffect } from '#imports'
import posthog from 'posthog-js'

export default defineNuxtPlugin((nuxtApp) => {
    const userStore = useUserStore();
    const runtimeConfig = useRuntimeConfig();
    const posthogClient = posthog.init(runtimeConfig.public.posthogPublicKey as string, {
        api_host: runtimeConfig.public.posthogHost as string,
        defaults: '2025-05-24',
        person_profiles: 'identified_only',
        capture_pageview: false,
        capture_pageleave: true,
        loaded: (posthog) => {
            if (import.meta.env.MODE === 'development') {
                posthog.debug();
            }
        }
    })

    const router = useRouter();
    router.afterEach((to) => {
        nextTick(() => {
            posthog.capture('$pageview', {
                current_url: to.fullPath
            });
        });
    });

    // Watch for user changes and identify/reset accordingly
    let isIdentified = false;

    watchEffect(() => {
        if (userStore.user && !isIdentified) {
            // User logged in - identify them
            posthogClient.identify(userStore.user.id, {
                name: userStore.user.name,
                email: userStore.user.email,
            });
            isIdentified = true;
        } else if (!userStore.user && isIdentified) {
            // User logged out - reset PostHog
            posthogClient.reset();
            isIdentified = false;
        }
    });

    // Error tracking - capture Vue errors
    nuxtApp.hook('vue:error', (err: unknown, instance, info: string) => {
        // Convert unknown error to Error object if possible
        const error = err instanceof Error ? err : new Error(String(err))
        posthogClient.captureException(error, {
            instance: instance ? instance.$options.name || 'Unknown Component' : null,
            info: info,
            source: 'vue:error'
        })
    })

    // Error tracking - capture app errors
    nuxtApp.hook('app:error', (error: Error) => {
        posthogClient.captureException(error, {
            source: 'app:error'
        })
    })

    return {
        provide: {
            posthog: posthogClient
        }
    }
})